
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { contactSchema, type ContactFormData } from "./validation-schemas";
import { useTranslation } from "react-i18next";
import { sendToGoogleChat } from "@/utils/googleChat";
import { Users, Building2, MessageSquare } from "lucide-react";

const GOOGLE_CHAT_WEBHOOK_URL = "VOTRE_WEBHOOK_URL_ICI";

const ContactForm = () => {
  const { t } = useTranslation('contact');
  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      company: "",
      message: "",
    },
  });

  const onSubmit = async (data: ContactFormData) => {
    try {
      const formattedMessage = `
🎉 *Nouveau message de contact !* 🎉

👤 *Contact*
• Nom : ${data.firstName} ${data.lastName}
• Email : ${data.email}
${data.company ? `\n🏢 *Entreprise*\n• ${data.company}` : ''}

💌 *Message*
${data.message}

⏰ Reçu le ${new Date().toLocaleString('fr-FR')}

_Une nouvelle personne s'intéresse à nous, c'est super ! N'oublions pas de lui répondre rapidement ! 😊_
`;

      await sendToGoogleChat(GOOGLE_CHAT_WEBHOOK_URL, {
        text: formattedMessage,
      });

      console.log(data);
      toast.success(t("form.success"));
      form.reset();
    } catch (error) {
      console.error(error);
      toast.error(t("form.error"));
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div className="animate-fade-in [--animate-delay:200ms]">
          <div className="flex items-center gap-2 text-lg font-medium text-brand-blue dark:text-brand-orange mb-4">
            <Users className="w-5 h-5" />
            Informations personnelles
          </div>
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.firstName")}</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder={t("form.firstName_example")} 
                      className="focus-visible:ring-brand-orange/50"
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.lastName")}</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder={t("form.lastName_example")} 
                      className="focus-visible:ring-brand-orange/50"
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.email")}</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder={t("form.email_example")} 
                      className="focus-visible:ring-brand-orange/50"
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <div className="animate-fade-in [--animate-delay:400ms]">
          <div className="flex items-center gap-2 text-lg font-medium text-brand-blue dark:text-brand-orange mb-4">
            <Building2 className="w-5 h-5" />
            Informations de l'entreprise
          </div>
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="company"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.company")}</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder={t("form.company_example")} 
                      className="focus-visible:ring-brand-orange/50"
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <div className="animate-fade-in [--animate-delay:600ms]">
          <div className="flex items-center gap-2 text-lg font-medium text-brand-blue dark:text-brand-orange mb-4">
            <MessageSquare className="w-5 h-5" />
            Votre message
          </div>
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.message")}</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder={t("form.message_example")} 
                      className="min-h-[150px] focus-visible:ring-brand-orange/50"
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <button 
          type="submit"
          className="w-full group bg-gradient-to-r from-brand-blue to-brand-orange text-white inline-flex justify-center items-center px-6 py-3 rounded-lg hover:opacity-90 transition-all duration-300 shadow-lg hover:shadow-xl"
        >
          {t("form.submit")}
        </button>
      </form>
    </Form>
  );
};

export default ContactForm;
